import React from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo.png`;

const propertyLinks = [
  ['Properties for sale', '/properties?types=sale'],
  ['Properties to rent', '/properties?types=rent'],
  ['Selling your property', '/sellers'],
  ['Landlord services', '/landlords'],
];

const supportLinks = [
  ['Book a valuation', '/sellers/valuation'],
  ['Report a repair', '/repairs'],
  ['Make a payment', '/payments'],
  ['Contact us', '/contact'],
];

export default function Footer() {
  return (
    <Box component="footer" sx={{ bgcolor: '#0b172a', color: '#fff' }}>
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2.5}
          sx={{ py: { xs: 4, md: 4.5 }, borderBottom: '1px solid rgba(255,255,255,.12)' }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.65rem', md: '2rem' }, color: '#fff' }}>
              Thinking of moving?
            </Typography>
            <Typography sx={{ mt: 0.75, color: 'rgba(255,255,255,.68)' }}>
              Start with straightforward advice from a local Middlesbrough property team.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Button component={RouterLink} to="/sellers/valuation" variant="contained" sx={{ bgcolor: '#fff', color: '#10234b', '&:hover': { bgcolor: '#edf2f7' } }}>
              Book a valuation
            </Button>
            <Button component={RouterLink} to="/properties" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.45)', '&:hover': { borderColor: '#fff' } }}>
              Search properties
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={{ xs: 4, md: 6 }} sx={{ py: { xs: 5, md: 7 } }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box component="img" src={LOGO_SRC} alt="MRM Lettings" sx={{ width: 54, height: 54, mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#fff' }}>MRM Lettings</Typography>
            <Typography sx={{ mt: 1.25, maxWidth: 360, color: 'rgba(255,255,255,.66)', lineHeight: 1.75 }}>
              Sales, lettings and property management across Middlesbrough and the wider Teesside area.
            </Typography>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <FooterHeading>Property</FooterHeading>
            <Stack spacing={1.1}>{propertyLinks.map(([label, to]) => <FooterLink key={label} to={to}>{label}</FooterLink>)}</Stack>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <FooterHeading>Support</FooterHeading>
            <Stack spacing={1.1}>{supportLinks.map(([label, to]) => <FooterLink key={label} to={to}>{label}</FooterLink>)}</Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <FooterHeading>Middlesbrough office</FooterHeading>
            <Stack spacing={1.25} sx={{ color: 'rgba(255,255,255,.72)' }}>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <LocationOnOutlined sx={{ fontSize: 20, mt: 0.2 }} />
                <Typography variant="body2">142 Victoria Road<br />Middlesbrough, TS1 3HU</Typography>
              </Stack>
              <MuiLink href="tel:+441642243378" color="inherit" underline="hover" sx={contactLinkSx}>
                <PhoneOutlined sx={{ fontSize: 19 }} /> 01642 243 378
              </MuiLink>
              <MuiLink href="mailto:info@mrmlettings.co.uk" color="inherit" underline="hover" sx={contactLinkSx}>
                <EmailOutlined sx={{ fontSize: 19 }} /> info@mrmlettings.co.uk
              </MuiLink>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,.12)' }} />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ py: 3, color: 'rgba(255,255,255,.56)' }}
        >
          <Typography variant="caption">© {new Date().getFullYear()} MRM Lettings Property &amp; Management Ltd.</Typography>
          <Stack direction="row" spacing={2.5}>
            <FooterLink to="/privacy">Privacy</FooterLink>
            <FooterLink to="/terms">Terms</FooterLink>
            <FooterLink to="/cookies">Cookies</FooterLink>
          </Stack>
        </Stack>
      </Container>

    </Box>
  );
}

function FooterHeading({ children }) {
  return <Typography variant="subtitle2" sx={{ mb: 1.75, color: '#fff', fontWeight: 800 }}>{children}</Typography>;
}

function FooterLink({ to, children }) {
  return (
    <MuiLink component={RouterLink} to={to} color="inherit" underline="hover" sx={{ width: 'fit-content', color: 'rgba(255,255,255,.66)', fontSize: '0.875rem', '&:hover': { color: '#fff' } }}>
      {children}
    </MuiLink>
  );
}

const contactLinkSx = { display: 'inline-flex', alignItems: 'center', gap: 1.2, width: 'fit-content', fontSize: '0.875rem' };
